import { Component, ChangeDetectionStrategy, inject, AfterViewInit, ElementRef } from '@angular/core';
import { CommonModule, Location } from '@angular/common';
import { RouterLink, Router } from '@angular/router';
import { combineLatest, map } from 'rxjs';
import { FooterComponent } from '../footer/footer.component';
import { AuthService } from '../../services/auth.service';
import { CompanyInfoService } from '../../services/company-info.service';
import { ConfigService } from '../../services/config.service';

@Component({
  selector: 'app-imprint',
  standalone: true,
  imports: [CommonModule, RouterLink, FooterComponent],
  template: `
    <main>
      <div class="header-container">
        <div class="back-link">
          <a (click)="goBack()" style="cursor: pointer; color: oklch(51% 0.28 263.83); font-size: 0.875rem;">← Zurück</a>
        </div>
        <div class="header-content">
          <a (click)="goHome()" style="cursor: pointer; text-decoration: none; display: inline-block;">
            <h1>ProTyco</h1>
            <p style="font-size: 0.875rem; text-align: center; margin: 0.25rem 0 0 0;">
              <span style="color: oklch(51% 0.28 263.83);">PRO</span>PERTY <span style="color: oklch(51% 0.28 263.83);">TYCO</span>ON
            </p>
          </a>
        </div>
      </div>

      <div class="imprint-scroll">
      <div class="container">
        <div class="content-card">
          <h1>Impressum</h1>
          
          <section class="imprint-section">
            <h2>Angaben gemäß § 5 TMG</h2>
            <p>
              <strong>{{ (companyInfo$ | async)?.name }}</strong><br>
              {{ (companyInfo$ | async)?.street }}<br>
              {{ (companyInfo$ | async)?.postalCode }} {{ (companyInfo$ | async)?.city }}<br>
              {{ (companyInfo$ | async)?.country }}
            </p>
          </section>

          <section class="imprint-section">
            <h2>Vertreten durch</h2>
            <p>
              {{ (representativesPerson$ | async) }}
            </p>
          </section>

          <section class="imprint-section">
            <h2>Kontakt</h2>
            <p>
              Telefon: <a [href]="'tel:' + ((companyInfo$ | async)?.phone || '').replace(/[^0-9+]/g, '')">{{ (companyInfo$ | async)?.phone }}</a><br>
              E-Mail: <a [href]="'mailto:' + (companyInfo$ | async)?.email">{{ (companyInfo$ | async)?.email }}</a>
            </p>
          </section>

          <section class="imprint-section">
            <h2>Registereintrag</h2>
            <p [innerHTML]="(registryEntry$ | async)"></p>
          </section>

          <section class="imprint-section">
            <h2>Umsatzsteuer-ID</h2>
            <p [innerHTML]="(taxNumber$ | async)"></p>
          </section>

          <section class="imprint-section">
            <h2>Verantwortlich für den Inhalt nach § 18 Abs. 2 MStV</h2>
            <p>
              {{ (representativesPersonResponsible$ | async) }}<br>
              {{ (companyInfo$ | async)?.street }}<br>
              {{ (companyInfo$ | async)?.postalCode }} {{ (companyInfo$ | async)?.city }}
            </p>
          </section>

          <section class="imprint-section">
            <h2>Datenschutz</h2>
            <p>
              Informationen zum Datenschutz finden Sie in unserer 
              <a routerLink="/privacy">Datenschutzerklärung</a>.
            </p>
          </section>
        </div>
      </div>
      </div>
    </main>

    <app-footer></app-footer>
  `,
  styles: [`
    :host {
      font-family: "Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", Roboto,
        Helvetica, Arial, sans-serif;
      box-sizing: border-box;
      display: flex;
      flex-direction: column;
      height: 100vh;
      overflow: hidden;
    }

    main {
      flex: 1;
      width: 100%;
      display: flex;
      flex-direction: column;
      gap: 0;
      padding: 0 0 1.5rem;
      min-height: 0;
    }

    .imprint-scroll {
      flex: 1;
      min-height: 0;
      overflow-y: auto;
      padding: 22px 1.5rem 0;
    }

    .header-container {
      display: flex;
      justify-content: space-between;
      align-items: flex-start;
      background: white;
      color: white;
      padding: 1rem 2rem 1rem 2rem;
      margin: 0;
      border-radius: 0;
      box-shadow: none;
      border-bottom: 1px solid #d1d5db;
      position: relative;
      z-index: 10;
      overflow: visible;
    }

    .header-container::before {
      content: '';
      position: absolute;
      top: 0;
      left: 0;
      right: 0;
      height: 1px;
      background: linear-gradient(90deg, transparent, rgba(255, 255, 255, 0.4), transparent);
      pointer-events: none;
    }

    .back-link {
      position: absolute;
      left: 5px;
      top: 37px;
      z-index: 10;
      pointer-events: none;
    }

    .back-link a {
      pointer-events: auto;
    }

    .back-link a:hover {
      opacity: 0.7;
      text-decoration: underline;
    }

    .back-link a:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .header-content {
      flex: 1;
      text-align: center;
      position: relative;
      overflow: visible;
    }

    .header-content::before {
      content: '';
      position: absolute;
      top: -15px;
      left: 50%;
      transform: translateX(-50%);
      width: 80px;
      height: 40px;
      background: linear-gradient(135deg, oklch(60% 0.3 280) 0%, oklch(45% 0.28 263.83) 100%);
      border-radius: 50%;
      opacity: 0.1;
      filter: blur(20px);
      z-index: 0;
    }

    .header-content h1 {
      background: linear-gradient(135deg, oklch(45% 0.28 263.83) 0%, oklch(60% 0.3 280) 50%, oklch(45% 0.28 263.83) 100%);
      -webkit-background-clip: text;
      -webkit-text-fill-color: transparent;
      background-clip: text;
      font-size: 3rem;
      margin: 0;
      padding-top: 0.5rem;
      padding-bottom: 0.5rem;
      font-weight: 900;
      letter-spacing: 0px;
      position: absolute;
      top: -32px;
      left: 50%;
      transform: translateX(calc(-50% + 7px));
      white-space: nowrap;
    }

    .header-content p {
      color: oklch(28% 0.06 263.83);
      margin: 43px 0 0 15px !important;
      padding-bottom: 0rem;
      font-weight: 600;
      font-size: 0.75rem;
      letter-spacing: 1px;
      text-transform: uppercase;
      line-height: 1.4;
    }

    .header-content p span {
      position: relative;
    }

    .header-content a:focus {
      outline: 2px solid oklch(51% 0.28 263.83);
      outline-offset: 2px;
      border-radius: 2px;
    }

    .container {
      flex: 1;
      max-width: 900px;
      margin: 0 auto;
      width: 100%;
    }

    .content-card {
      background-color: white;
      border: 1px solid #e5e7eb;
      border-radius: 0.5rem;
      padding: 2rem;
      box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1);
    }

    .content-card h1 {
      margin-top: 0;
      color: #1f2937;
      border-bottom: 2px solid oklch(51% 0.28 263.83);
      padding-bottom: 1rem;
    }

    .imprint-section {
      margin-bottom: 2rem;
      padding-bottom: 1.5rem;
      border-bottom: 1px solid #e5e7eb;
    }

    .imprint-section:last-child {
      border-bottom: none;
    }

    .imprint-section h2 {
      color: oklch(51% 0.28 263.83);
      margin-top: 0;
      margin-bottom: 0.75rem;
    }

    .imprint-section h3 {
      color: #374151;
      margin-top: 1rem;
      margin-bottom: 0.5rem;
      font-size: 0.95rem;
    }

    .imprint-section p {
      color: #6b7280;
      line-height: 1.6;
      margin: 0.5rem 0;
    }

    .imprint-section a {
      color: oklch(51% 0.28 263.83);
      text-decoration: none;
    }

    .imprint-section a:hover {
      text-decoration: underline;
    }

    @media (max-width: 768px) {
      main {
        padding: 0 0 1rem;
        gap: 0;
      }

      .imprint-scroll {
        padding: 22px 1rem 0;
      }

      .header-content h1 {
        font-size: 2rem;
        padding-top: 1rem;
      }

      .header-content p {
        font-size: 0.5625rem !important;
        margin: 32px 0 0 15px !important;
      }

      .content-card {
        padding: 1rem;
      }
    }
  `],
  changeDetection: ChangeDetectionStrategy.OnPush
})
export class ImprintComponent implements AfterViewInit {
  private location = inject(Location);
  private router = inject(Router);
  private authService = inject(AuthService);
  private elementRef = inject(ElementRef);
  private configService = inject(ConfigService);
  
  companyInfoService = inject(CompanyInfoService);
  companyInfo$ = this.companyInfoService.getCompanyInfo();
  representativesPerson$ = combineLatest([
    this.configService.getConfig('Company:VertretenDurchLabel'),
    this.configService.getConfig('Company:VertretenDurch')
  ]).pipe(
    map(([label, value]) => {
      if (!label && !value) return null;
      if (label && value) return `${label} ${value}`;
      return label || value;
    })
  );
  representativesPersonResponsible$ = this.configService.getConfig('Company:VertretenDurch');
  registryEntry$ = this.configService.getConfig('Company:Registereintrag');
  taxNumber$ = this.configService.getConfig('Company:UStID');
  ngAfterViewInit() {
    setTimeout(() => {
      window.scrollTo(0, 0);
      const scrollElement = this.elementRef.nativeElement.querySelector('.imprint-scroll');
      if (scrollElement) {
        scrollElement.scrollTop = 0;
      }
      document.documentElement.scrollTop = 0;
      document.body.scrollTop = 0;
    }, 100);
  }

  goBack() {
    this.location.back();
  }

  goHome() {
    if (this.authService.isLoggedIn()) {
      this.router.navigate(['/calculator']);
    } else {
      this.router.navigate(['/']);
    }
  }
}
